/** Lienzo de la Pizarra Inteligente 2.0: imagen + zoom/pan/rotación/pinch + marcadores con
 *  anclaje y flecha + tarjeta flotante arrastrable. La imagen original nunca se altera.
 *  Toque breve = abrir editor · arrastrar = mover marcador · punto pequeño = anclaje. */
import { useEffect, useRef, useState } from 'react';
import { PlanoSVG } from './PlanoInteligente';

export const COLOR_ESTADO = { pendiente: '#7352B5', confirmado: '#10B981', incompleto: '#F59E0B', error: '#EF4444' };
export const ICONO_TIPO = { vidrio: '🪟', puerta: '🚪', bisagra: '⚙️', clip: '🔩', jalador: '🖐️', perfil: '📏', cerradura: '🔒', union: '∠', anotacion: '📝', otro: '📦' };

const clamp = (v, a, b) => Math.min(Math.max(v, a), b);

export const LienzoPizarra = ({ imagen, elementos, trazos, mediciones = [], seleccionado, herramienta, rotacion, dialogo, focusReq, verMarcadores = true, previewOrg = null, vista = 'foto', plano = null, selPanel = null, onSelectPanel, onSelect, onAdd, onMove, onMoveEnd, onTrazo, onMediciones }) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [trazoActivo, setTrazoActivo] = useState(null);
  const [medStart, setMedStart] = useState(null);
  const [medCursor, setMedCursor] = useState(null);
  const [calib, setCalib] = useState(null);
  const [calibVal, setCalibVal] = useState('');
  const [dlgPos, setDlgPos] = useState(null);
  const [dlgModo, setDlgModo] = useState('full');
  const drag = useRef(null);
  const dlgDrag = useRef(null);
  const dlgMoved = useRef(false);
  const pendingAdd = useRef(null);
  const imgRef = useRef(null);
  const contRef = useRef(null);
  const pointers = useRef(new Map());
  const pinch = useRef(null);

  useEffect(() => { setDlgPos(null); setDlgModo('full'); }, [seleccionado]);

  const rot = ((rotacion % 360) + 360) % 360;
  // Encuadre: centra y escala el contenido útil sin espacios vacíos
  const ajustar = () => {
    const img = imgRef.current, cont = contRef.current;
    if (!img || !cont || !img.offsetWidth) return;
    const sw = rot % 180 === 0 ? img.offsetWidth : img.offsetHeight;
    const sh = rot % 180 === 0 ? img.offsetHeight : img.offsetWidth;
    setZoom(clamp(Math.min(cont.offsetWidth / Math.max(sw, 1), cont.offsetHeight / Math.max(sh, 1)) * 0.9, 0.2, 4));
    setPan({ x: 0, y: 0 });
  };
  useEffect(() => { const t = setTimeout(ajustar, 80); return () => clearTimeout(t); }, [vista]); // eslint-disable-line
  // coordenadas duales: foto (x,y/lx,ly) · plano (px,py/plx,ply)
  const anc = (el) => (vista === 'plano' ? { x: el.px, y: el.py } : { x: el.x, y: el.y });
  const aspect = () => { const el = imgRef.current; return el ? el.offsetWidth / Math.max(el.offsetHeight, 1) : 1; };
  const distN = (a, b) => Math.hypot((b[0] - a[0]) * aspect(), b[1] - a[1]);

  // Posición en px del contenedor para un punto normalizado de la imagen (sync con zoom/pan/giro)
  const posPx = (nx, ny) => {
    const img = imgRef.current, cont = contRef.current;
    if (!img || !cont || nx == null) return null;
    const dx = (nx - 0.5) * img.offsetWidth, dy = (ny - 0.5) * img.offsetHeight;
    const r = (rot * Math.PI) / 180;
    return { x: cont.offsetWidth / 2 + pan.x + (dx * Math.cos(r) - dy * Math.sin(r)) * zoom,
      y: cont.offsetHeight / 2 + pan.y + (dx * Math.sin(r) + dy * Math.cos(r)) * zoom,
      cw: cont.offsetWidth, ch: cont.offsetHeight };
  };

  // Centrar un elemento (desde la planilla)
  useEffect(() => {
    if (!focusReq?.id) return;
    const el = (elementos || []).find((e) => e.id === focusReq.id);
    const img = imgRef.current;
    const a = el ? anc(el) : null;
    if (!el || !img || a?.x == null) return;
    const nx = (vista === 'plano' ? el.plx : el.lx) ?? a.x, ny = (vista === 'plano' ? el.ply : el.ly) ?? a.y;
    const dx = (nx - 0.5) * img.offsetWidth, dy = (ny - 0.5) * img.offsetHeight;
    const r = (rot * Math.PI) / 180;
    setPan({ x: -(dx * Math.cos(r) - dy * Math.sin(r)) * zoom, y: -(dx * Math.sin(r) + dy * Math.cos(r)) * zoom });
  }, [focusReq?.tick]); // eslint-disable-line

  const relPos = (e) => {
    const r = imgRef.current?.getBoundingClientRect();
    if (!r) return null;
    const u = (e.clientX - r.left) / r.width;
    const v = (e.clientY - r.top) / r.height;
    if (rot === 90) return { x: v, y: 1 - u };
    if (rot === 180) return { x: 1 - u, y: 1 - v };
    if (rot === 270) return { x: 1 - v, y: u };
    return { x: u, y: v };
  };

  const down = (e) => {
    e.preventDefault();
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { d: Math.hypot(a.x - b.x, a.y - b.y), z: zoom, cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2, px: pan.x, py: pan.y };
      drag.current = null; setTrazoActivo(null); setMedStart(null); setMedCursor(null);
      return;
    }
    const p = relPos(e);
    // Agregar se confirma en el click (tras soltar): el diálogo se monta DESPUÉS del
    // click sintético y ningún chip recibe el toque fantasma (iPhone/Android)
    if (herramienta === 'agregar' && p) { pendingAdd.current = { x: p.x, y: p.y, sx: e.clientX, sy: e.clientY }; return; }
    if (vista === 'foto' && herramienta === 'dibujar' && p) { setTrazoActivo([[p.x, p.y]]); return; }
    if (vista === 'foto' && herramienta === 'medir' && p) { setMedStart([p.x, p.y]); setMedCursor([p.x, p.y]); return; }
    if (selPanel) onSelectPanel?.(null);
    if (seleccionado) onSelect(null); // tocar fuera cierra la tarjeta (los cambios ya están en estado)
    drag.current = { sx: e.clientX, sy: e.clientY, px: pan.x, py: pan.y, marker: null };
  };

  // Toque breve = abrir editor · arrastrar >8px = mover (etiqueta o anclaje) sin abrir
  const downMarker = (e, el, kind = 'label') => {
    if (!['seleccionar', 'mover'].includes(herramienta)) return;
    e.stopPropagation(); e.preventDefault();
    drag.current = { marker: el.id, kind, sx: e.clientX, sy: e.clientY, moved: false };
  };

  const move = (e) => {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (d > 10) setZoom(Math.min(4, Math.max(0.4, pinch.current.z * (d / pinch.current.d))));
      setPan({ x: pinch.current.px + ((a.x + b.x) / 2 - pinch.current.cx), y: pinch.current.py + ((a.y + b.y) / 2 - pinch.current.cy) });
      return;
    }
    if (medStart) { const p = relPos(e); if (p) setMedCursor([p.x, p.y]); return; }
    if (pendingAdd.current && Math.hypot(e.clientX - pendingAdd.current.sx, e.clientY - pendingAdd.current.sy) > 8) pendingAdd.current = null;
    if (trazoActivo) { const p = relPos(e); if (p) setTrazoActivo((t) => [...t, [p.x, p.y]]); return; }
    if (!drag.current) return;
    if (drag.current.marker) {
      if (!drag.current.moved && Math.hypot(e.clientX - drag.current.sx, e.clientY - drag.current.sy) < 8) return;
      drag.current.moved = true;
      const p = relPos(e);
      if (p) onMove(drag.current.marker, p.x, p.y, drag.current.kind);
      return;
    }
    setPan({ x: drag.current.px + (e.clientX - drag.current.sx), y: drag.current.py + (e.clientY - drag.current.sy) });
  };

  const up = (e) => {
    if (e?.pointerId !== undefined) pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (medStart && medCursor && distN(medStart, medCursor) > 0.015) {
      const ref = (mediciones || []).find((m) => m.calibrada);
      if (!ref) {
        setCalib({ a: medStart, b: medCursor }); // tarjeta inferior táctil (sin window.prompt)
      } else {
        const m = Math.round(ref.escala * distN(medStart, medCursor) * 100) / 100;
        onMediciones([...(mediciones || []), { a: medStart, b: medCursor, m, estimada: true }]);
      }
    }
    if (medStart) { setMedStart(null); setMedCursor(null); return; }
    if (trazoActivo) { if (trazoActivo.length > 2) onTrazo(trazoActivo); setTrazoActivo(null); }
    if (drag.current?.marker) {
      if (drag.current.moved) onMoveEnd?.();
      else onSelect(drag.current.marker); // toque breve abre el editor
    }
    drag.current = null;
  };

  const clickCont = () => {
    if (!pendingAdd.current) return;
    const { x, y } = pendingAdd.current;
    pendingAdd.current = null;
    onAdd(x, y);
  };

  const confirmarCalib = () => {
    const v = parseFloat(String(calibVal).replace(',', '.'));
    if (!(v > 0)) return;
    onMediciones([...(mediciones || []), { a: calib.a, b: calib.b, m: v, calibrada: true, escala: v / distN(calib.a, calib.b) }]);
    setCalib(null); setCalibVal('');
  };

  const visibles = (elementos || []).filter((el) => (vista === 'plano' ? el.px != null : !el.sin_ubicar && el.x != null));
  // posición visual efectiva (vista previa de organización tiene prioridad, solo visual)
  const posDe = (el) => {
    if (vista === 'plano') return { lx: el.plx, ly: el.ply };
    return (previewOrg && previewOrg[el.id]) ? previewOrg[el.id] : { lx: el.lx, ly: el.ly };
  };
  const elSel = visibles.find((el) => el.id === seleccionado);
  const pdSel = elSel ? posDe(elSel) : null;
  const aSel = elSel ? anc(elSel) : null;
  const pSel = elSel ? posPx(pdSel.lx ?? aSel.x, pdSel.ly ?? aSel.y) : null;
  const movil = pSel ? pSel.cw < 640 : false;
  const cardW = pSel ? (movil ? pSel.cw - 16 : Math.min(300, pSel.cw - 20)) : 300;
  let cardL = 0, cardT = 0;
  if (pSel) {
    if (dlgPos) { cardL = clamp(dlgPos.x, 4, pSel.cw - cardW - 4); cardT = clamp(dlgPos.y, 4, pSel.ch - 90); }
    else if (movil) { cardL = 8; cardT = pSel.y > pSel.ch - 310 ? 8 : Math.max(8, pSel.ch - 296); }
    else { cardL = pSel.x + 26 + cardW > pSel.cw - 8 ? Math.max(8, pSel.x - cardW - 26) : pSel.x + 26; cardT = clamp(pSel.y - 46, 8, Math.max(8, pSel.ch - 276)); }
  }
  const hdrDown = (e) => { e.stopPropagation(); e.preventDefault(); try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ok */ } dlgMoved.current = false; dlgDrag.current = { sx: e.clientX, sy: e.clientY, x: cardL, y: cardT }; };
  const hdrMove = (e) => {
    if (!dlgDrag.current) return;
    if (Math.hypot(e.clientX - dlgDrag.current.sx, e.clientY - dlgDrag.current.sy) > 6) dlgMoved.current = true;
    setDlgPos({ x: dlgDrag.current.x + (e.clientX - dlgDrag.current.sx), y: dlgDrag.current.y + (e.clientY - dlgDrag.current.sy) });
  };
  const hdrUp = () => { dlgDrag.current = null; };
  // Separa la etiqueta de su anclaje creando la flecha de dos extremos (no toca producto/cantidad)
  const separar = () => {
    if (!elSel || aSel?.x == null) return;
    const dx = aSel.x > 0.85 ? -0.1 : 0.1;
    const dy = aSel.y < 0.15 ? 0.1 : -0.1;
    onMove(elSel.id, aSel.x + dx, aSel.y + dy, 'label'); onMoveEnd?.();
  };

  return (
    <div ref={contRef} className="relative w-full h-full bg-[#F4EFFB] overflow-hidden rounded-[14px] border-2 border-[#B9A5D9]/50 select-none"
      style={{ touchAction: 'none' }} data-testid="pizarra-lienzo"
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} onClick={clickCont}>
      <div className="absolute top-1/2 left-1/2" style={{ transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px)) scale(${zoom}) rotate(${rot}deg)` }}>
        <div ref={imgRef} className="relative">
          {vista === 'plano' && plano ? (
            <div style={{ width: 'min(82vw, 720px)' }} data-testid="pizarra-plano-wrap">
              <PlanoSVG plano={plano} selPanel={selPanel} onSelectPanel={onSelectPanel} herramienta={herramienta} />
            </div>
          ) : (
            <img src={imagen} alt="dibujo" draggable={false} className="max-w-none pointer-events-none" onLoad={ajustar}
              style={{ width: 'min(82vw, 720px)' }} data-testid="pizarra-imagen" />
          )}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ overflow: 'visible' }}>
            {vista === 'foto' && (trazos || []).map((t, i) => (
              <polyline key={i} points={t.points.map(([x, y]) => `${x * 100},${y * 100}`).join(' ')}
                fill="none" stroke="#7352B5" strokeLinecap="round" vectorEffect="non-scaling-stroke" style={{ strokeWidth: 2 }} />
            ))}
            {vista === 'foto' && trazoActivo && (
              <polyline points={trazoActivo.map(([x, y]) => `${x * 100},${y * 100}`).join(' ')}
                fill="none" stroke="#38205F" vectorEffect="non-scaling-stroke" style={{ strokeWidth: 2 }} />
            )}
            {verMarcadores && visibles.map((el) => ({ el, pd: posDe(el), a: anc(el) })).filter(({ pd, a }) => pd.lx != null && (Math.abs(pd.lx - a.x) > 0.004 || Math.abs((pd.ly ?? a.y) - a.y) > 0.004)).map(({ el, pd, a }) => (
              <g key={`fl-${el.id}`}>
                <line x1={pd.lx * 100} y1={pd.ly * 100} x2={a.x * 100} y2={a.y * 100}
                  stroke={`${COLOR_ESTADO[el.estado] || '#7352B5'}BB`} vectorEffect="non-scaling-stroke" style={{ strokeWidth: 1.5 }} />
                <circle cx={a.x * 100} cy={a.y * 100} r="0.9" fill={COLOR_ESTADO[el.estado] || '#7352B5'} />
              </g>
            ))}
            {vista === 'foto' && (mediciones || []).map((m, i) => (
              <line key={`med${i}`} x1={m.a[0] * 100} y1={m.a[1] * 100} x2={m.b[0] * 100} y2={m.b[1] * 100}
                stroke={m.calibrada ? '#10B981' : '#F59E0B'} vectorEffect="non-scaling-stroke" style={{ strokeWidth: 2.5 }} strokeDasharray={m.calibrada ? '' : '6 3'} />
            ))}
            {calib && (
              <line x1={calib.a[0] * 100} y1={calib.a[1] * 100} x2={calib.b[0] * 100} y2={calib.b[1] * 100}
                stroke="#10B981" vectorEffect="non-scaling-stroke" style={{ strokeWidth: 2.5 }} strokeDasharray="6 3" />
            )}
            {medStart && medCursor && (
              <line x1={medStart[0] * 100} y1={medStart[1] * 100} x2={medCursor[0] * 100} y2={medCursor[1] * 100}
                stroke="#F59E0B" vectorEffect="non-scaling-stroke" style={{ strokeWidth: 2.5 }} strokeDasharray="6 3" />
            )}
          </svg>
          {vista === 'foto' && (mediciones || []).map((m, i) => (
            <span key={`medl${i}`} className="absolute px-1.5 py-0.5 rounded text-[9px] font-black shadow pointer-events-none"
              style={{ left: `${(m.a[0] + m.b[0]) / 2 * 100}%`, top: `${(m.a[1] + m.b[1]) / 2 * 100}%`,
                transform: `translate(-50%,-120%) rotate(${-rot}deg)`, zIndex: 15,
                background: m.calibrada ? '#10B981' : '#F59E0B', color: '#fff' }}
              data-testid={`pizarra-medicion-${i}`}>
              {m.calibrada ? `📏 ${m.m} m (referencia)` : `~${m.m} m estimado`}
            </span>
          ))}
          {/* Punto de anclaje reposicionable del marcador seleccionado */}
          {verMarcadores && elSel && pdSel?.lx != null && aSel?.x != null && (
            <button type="button" onPointerDown={(e) => downMarker(e, elSel, 'ancla')}
              className="absolute flex items-center justify-center"
              style={{ left: `${aSel.x * 100}%`, top: `${aSel.y * 100}%`, width: 36, height: 36,
                transform: 'translate(-50%,-50%)', background: 'transparent', touchAction: 'none', zIndex: 22,
                pointerEvents: ['seleccionar', 'mover'].includes(herramienta) ? 'auto' : 'none' }}
              data-testid={`pizarra-ancla-${elSel.etiqueta}`}>
              <span className="rounded-full pointer-events-none shadow" style={{ width: 13, height: 13, background: '#fff', border: `3px solid ${COLOR_ESTADO[elSel.estado] || '#7352B5'}` }} />
            </button>
          )}
          {verMarcadores && visibles.map((el) => {
            const pd = posDe(el), a = anc(el);
            return (
            <button key={el.id} type="button"
              onPointerDown={(e) => downMarker(e, el)}
              className="absolute flex items-center justify-center"
              style={{
                left: `${(pd.lx ?? a.x) * 100}%`, top: `${(pd.ly ?? a.y) * 100}%`, width: 44, height: 44, // zona táctil 44px — visual 24px
                pointerEvents: !previewOrg && ['seleccionar', 'mover'].includes(herramienta) ? 'auto' : 'none',
                transform: `translate(-50%,-50%) rotate(${-rot}deg)`,
                background: 'transparent', touchAction: 'none',
                zIndex: seleccionado === el.id ? 20 : 10,
              }}
              data-testid={`pizarra-marcador-${el.etiqueta}`}>
              <span className="flex items-center justify-center rounded-full font-black shadow-md pointer-events-none"
                style={{
                  width: 24, height: 24, fontSize: 9,
                  background: '#fff', color: COLOR_ESTADO[el.estado] || '#7352B5',
                  border: `2.5px solid ${COLOR_ESTADO[el.estado] || '#7352B5'}`,
                  boxShadow: seleccionado === el.id ? `0 0 0 4px ${COLOR_ESTADO[el.estado]}44` : undefined,
                  transform: seleccionado === el.id ? 'scale(1.4)' : undefined,
                }}>
                {el.etiqueta}
              </span>
            </button>
            );
          })}
        </div>
      </div>

      {/* Tarjeta flotante arrastrable: completa / compacta / minimizada */}
      {elSel && pSel && dialogo && (
        <>
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 39 }}>
            <line x1={pSel.x} y1={pSel.y} x2={clamp(pSel.x, cardL + 14, cardL + cardW - 14)} y2={cardT + 14}
              stroke={`${COLOR_ESTADO[elSel.estado] || '#7352B5'}99`} strokeWidth="1.5" strokeDasharray="4 3" />
            <circle cx={pSel.x} cy={pSel.y} r="4" fill={COLOR_ESTADO[elSel.estado] || '#7352B5'} />
          </svg>
          {dlgModo === 'min' ? (
            <button type="button"
              className="absolute rounded-full shadow-xl border-2 bg-white px-3.5 py-2 text-[11px] font-black text-[#38205F]"
              style={{ left: cardL, top: cardT, zIndex: 40, borderColor: `${COLOR_ESTADO[elSel.estado] || '#7352B5'}88`, touchAction: 'none' }}
              onPointerDown={hdrDown} onPointerMove={hdrMove} onPointerUp={hdrUp}
              onClick={() => { if (!dlgMoved.current) setDlgModo('full'); }}
              data-testid="pizarra-dialogo-min">▣ {elSel.etiqueta}</button>
          ) : (
            <div className="absolute bg-white border-2 rounded-[12px] shadow-2xl flex flex-col"
              style={{ left: cardL, top: cardT, width: cardW, maxHeight: Math.min(330, pSel.ch - cardT - 8), zIndex: 40, borderColor: `${COLOR_ESTADO[elSel.estado] || '#7352B5'}66` }}
              onPointerDown={(e) => e.stopPropagation()} data-testid="pizarra-dialogo-flotante">
              <div className="flex items-center justify-between gap-1 px-2 py-1 cursor-move select-none rounded-t-[10px] shrink-0"
                style={{ background: '#F4EFFB', touchAction: 'none' }}
                onPointerDown={hdrDown} onPointerMove={hdrMove} onPointerUp={hdrUp} data-testid="pizarra-dialogo-header">
                <span className="text-[10px] font-black text-[#38205F] truncate">⠿ {elSel.etiqueta} — arrastra para mover</span>
                <span className="flex items-center gap-0.5 shrink-0">
                  {pdSel?.lx == null && (
                    <button type="button" title="Separar etiqueta con flecha" onPointerDown={(e) => e.stopPropagation()} onClick={separar}
                      className="w-8 h-8 rounded-lg text-[13px] font-black text-[#7352B5] hover:bg-white" data-testid="pizarra-separar-flecha">↗</button>
                  )}
                  <button type="button" title={dlgModo === 'compacta' ? 'Ventana completa' : 'Ventana compacta'} onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => setDlgModo(dlgModo === 'compacta' ? 'full' : 'compacta')}
                    className="w-8 h-8 rounded-lg text-[13px] font-black text-[#38205F] hover:bg-white" data-testid="pizarra-dlg-compacta">{dlgModo === 'compacta' ? '▢' : '─'}</button>
                  <button type="button" title="Minimizar" onPointerDown={(e) => e.stopPropagation()} onClick={() => setDlgModo('min')}
                    className="w-8 h-8 rounded-lg text-[13px] font-black text-[#38205F] hover:bg-white" data-testid="pizarra-dlg-min">▁</button>
                </span>
              </div>
              {dlgModo === 'compacta' ? (
                <div className="p-2.5 space-y-1.5" data-testid="pizarra-dlg-compacta-body">
                  <p className="text-[12px] font-black text-[#38205F]">{ICONO_TIPO[elSel.tipo] || '📦'} {elSel.etiqueta} · {elSel.tipo}
                    <span className="ml-1.5 inline-block w-2.5 h-2.5 rounded-full align-middle" style={{ background: COLOR_ESTADO[elSel.estado] || '#7352B5' }} />
                  </p>
                  <p className="text-[11px] text-[#6B6B6B] truncate">{elSel.producto?.nombre || elSel.producto?.descripcion ? `🔗 ${elSel.producto?.nombre || elSel.producto?.descripcion}` : 'Sin producto vinculado'}</p>
                  <button type="button" onClick={() => setDlgModo('full')} className="w-full text-[11px] font-black text-white bg-[#7352B5] hover:bg-[#38205F] rounded-lg px-3 py-2" data-testid="pizarra-dlg-expandir">▢ Ver ficha completa</button>
                </div>
              ) : (
                <div className="p-2.5 overflow-y-auto">{dialogo}</div>
              )}
            </div>
          )}
        </>
      )}

      {/* Tarjeta inferior de calibración — táctil, reemplaza window.prompt */}
      {calib && (
        <div className="absolute left-2 right-2 bottom-2 bg-white border-2 border-emerald-500/60 rounded-[12px] shadow-2xl p-3 z-50"
          onPointerDown={(e) => e.stopPropagation()} data-testid="pizarra-calibrar-card">
          <p className="text-[11px] font-black text-[#1F1B29]">📐 Calibración: ¿cuántos metros mide esta línea en la realidad?</p>
          <div className="flex gap-1.5 mt-2">
            <input type="text" inputMode="decimal" placeholder="Ej. 0.90" value={calibVal} autoFocus
              onChange={(e) => setCalibVal(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && confirmarCalib()}
              className="flex-1 min-w-0 h-10 px-2.5 text-sm rounded-lg border border-[#B9A5D9]/60 outline-none focus:border-emerald-500" data-testid="pizarra-calibrar-input" />
            <button type="button" onClick={confirmarCalib} className="h-10 px-4 rounded-lg bg-emerald-500 text-white text-xs font-black" data-testid="pizarra-calibrar-ok">Calibrar</button>
            <button type="button" onClick={() => { setCalib(null); setCalibVal(''); }} className="h-10 px-3 rounded-lg border border-[#B9A5D9]/60 text-[#6B6B6B] text-xs font-bold" data-testid="pizarra-calibrar-cancelar">✕</button>
          </div>
        </div>
      )}

      <div className="absolute bottom-2 right-2 flex gap-1 z-30">
        {[['−', () => setZoom((z) => Math.max(0.2, z - 0.25)), 'zoom-out'],
          ['+', () => setZoom((z) => Math.min(4, z + 0.25)), 'zoom-in'],
          ['⤢', ajustar, 'zoom-fit'],
          ['⟳', () => { setPan({ x: 0, y: 0 }); setZoom(1); }, 'zoom-reset']].map(([l, fn, id]) => (
          <button key={id} type="button" onPointerDown={(e) => e.stopPropagation()} onClick={fn}
            className="w-9 h-9 rounded-lg bg-white border border-[#B9A5D9]/60 text-[#38205F] font-black shadow" data-testid={`pizarra-${id}`}>{l}</button>
        ))}
      </div>
    </div>
  );
};
